import { useRef } from 'react';
import { Upload } from 'lucide-react';
import { useUploadHeaderLogo } from '../../api/layout/useHeaderConfigApi';
import { useToast } from '../../context/ToastContext';
import { UPLOAD_BASE_URL } from '../../utils/ip';
import styles from './HeaderSettings.module.css';

/** A stored logo path is relative to the API host; anything else is used as it is. */
export function resolveLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith('/')) return `${UPLOAD_BASE_URL}${url}`;
  return url;
}

export default function LogoConfigSection({ logoUrl, logoAltText, logoLinkTarget, onFieldChange, wordmark, onWordmarkChange, logoHeight, onLogoHeightChange }) {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const wordmarkInputRef = useRef(null);
  const { uploadLogo, loading: uploadLoading, error: uploadError } = useUploadHeaderLogo();

  const displayUrl = resolveLogoUrl(logoUrl) || '/logo.png';

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadLogo(file);
      if (url) {
        onFieldChange('logoUrl', url);
        showToast('Logo uploaded. Save the header to publish it.', 'success');
      } else {
        showToast('The upload returned no logo address', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to upload logo', 'error');
    }
    e.target.value = '';
  };

  // The wordmark (the brand name beside the icon) goes through the same upload endpoint.
  const handleWordmarkChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadLogo(file);
      if (url) {
        onWordmarkChange({ url });
        showToast('Wordmark uploaded. Save the header to publish it.', 'success');
      } else {
        showToast('The upload returned no image address', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to upload the wordmark', 'error');
    }
    e.target.value = '';
  };
  const wordmarkUrl = resolveLogoUrl(wordmark?.url);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <div className={styles.sectionIcon}>🖼</div>
          <div>
            <h3>Logo Configuration</h3>
            <p>Upload and configure your site logo</p>
          </div>
        </div>
      </div>

      <div className={styles.logoBlock}>
        <label className={styles.label}>Logo Image</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <div className={styles.logoRow}>
          <div className={styles.logoPreview}>
            <img src={displayUrl} alt={logoAltText || 'logo'} />
          </div>
          <div className={styles.logoActions}>
            <button
              type="button"
              className={styles.uploadBtn}
              onClick={handleUploadClick}
              disabled={uploadLoading}
            >
              <Upload size={14} />
              {uploadLoading ? 'Uploading…' : 'Upload New Logo'}
            </button>
            {uploadError && (
              <span className={styles.hint} style={{ color: '#b91c1c' }}>
                {uploadError}
              </span>
            )}
            <span className={styles.hint}>Recommended: PNG or SVG, max 200×80px. Max 2MB.</span>
            {onLogoHeightChange && (
              <div className={styles.field} style={{ marginTop: 8 }}>
                <label>Logo Height (px, 20–80)</label>
                <input
                  type="number"
                  min={20}
                  max={80}
                  placeholder="Automatic"
                  value={logoHeight ?? ''}
                  onChange={(e) => onLogoHeightChange(e.target.value === '' ? null : Number(e.target.value))}
                />
                <span className={styles.hint}>Empty = automatic (40px icon, wide logo up to 64px). Phones show at most 44px.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {onWordmarkChange && (
        <div className={styles.logoBlock}>
          <label className={styles.label}>Wordmark Image (beside the logo)</label>
          <input
            ref={wordmarkInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
            onChange={handleWordmarkChange}
            style={{ display: 'none' }}
          />
          <div className={styles.logoRow}>
            <div className={styles.logoPreview}>
              {wordmarkUrl
                ? <img src={wordmarkUrl} alt={wordmark?.alt || 'wordmark'} />
                : <span className={styles.hint}>Text “SunSky”</span>}
            </div>
            <div className={styles.logoActions}>
              <button type="button" className={styles.uploadBtn} onClick={() => wordmarkInputRef.current?.click()} disabled={uploadLoading}>
                <Upload size={14} />
                {uploadLoading ? 'Uploading…' : wordmarkUrl ? 'Replace Wordmark' : 'Upload Wordmark'}
              </button>
              {wordmarkUrl && (
                <button type="button" className={styles.uploadBtn} onClick={() => onWordmarkChange({ url: '' })}>
                  Remove (show the text again)
                </button>
              )}
              <span className={styles.hint}>The brand name shown beside the logo icon. Without an image the site shows the text “SunSky”. Recommended: transparent PNG or SVG, about 4:1, max 2MB.</span>
            </div>
          </div>
          <div className={styles.formRow} style={{ marginTop: 10 }}>
            <div className={styles.field}>
              <label>Wordmark Alt Text</label>
              <input value={wordmark?.alt ?? ''} onChange={(e) => onWordmarkChange({ alt: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>Wordmark Height (px, 20–64)</label>
              <input
                type="number"
                min={20}
                max={64}
                value={wordmark?.height ?? 46}
                onChange={(e) => onWordmarkChange({ height: e.target.value === '' ? '' : Number(e.target.value) })}
              />
              <span className={styles.hint}>Height on desktop; phones show at most 36px. Default 46.</span>
            </div>
          </div>
        </div>
      )}

      <div className={styles.formRow}>
        <div className={styles.field}>
          <label>Alt Text</label>
          <input
            value={logoAltText ?? ''}
            onChange={(e) => onFieldChange('logoAltText', e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>Link Target</label>
          <input
            value={logoLinkTarget ?? ''}
            onChange={(e) => onFieldChange('logoLinkTarget', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
